import React, { useEffect, useState, useContext } from "react"
import hotkeys from "hotkeys-js"

import ShowPlateView from "./mosaic_page/show_plate_view"
import IdeaPane from "./mosaic_page/idea_pane"
import FuzzyIdeaFinder from "../../components/fuzzy_idea_finder"
import { ID, Idea, Location } from "../../../model/types"
import { useNana } from "../../hooks/useNana"
import { DeviceContext, KEYS } from "../../providers/device_context"
import * as Commands from "../../../model/commands"
import { ViewBounds } from "../../../plate_viewer"
import type { IdeaColourMode } from "../../../plate_viewer/idea_colour"
import NavigationHistory from "./mosaic_page/navigation_history"

export var NavigationHistoryState = new NavigationHistory()

export const MosaicPageState: {
  locked: boolean
  escapeShouldCloseEditor: boolean
  forceOpenLocation: Location | null
  /** When set, open the fleeting ideas panel on next mosaic mount. */
  openFleetingPanel: boolean
  /** Prefer scrolling/highlighting this fleeting id in the panel. */
  highlightFleetingId: ID | null
} = {
  locked: false,

  // Should an escape keypress in the editor cause the idea pane to close?
  escapeShouldCloseEditor: true,

  forceOpenLocation: null,
  openFleetingPanel: false,
  highlightFleetingId: null,
}

export default function Mosaic() {
  const { model, commander, hub, plateView, local, sync, initialLoadComplete } =
    useNana()

  useEffect(() => {
    NavigationHistoryState.model = model
    NavigationHistoryState.attachToHub(hub)
  }, [])

  const { platform } = useContext(DeviceContext)

  const [plateViewDraggingHappening, setPlateViewDraggingHappening] =
    useState(false)
  const [openIdeaId, setOpenIdeaId] = useState<ID | null | undefined>(undefined)
  const [initialViewBounds, setInitialViewBounds] = useState<
    ViewBounds | null | undefined
  >(undefined)
  const [selectionPos, setSelectionPos] = useState(0)
  const [locked, setLocked] = useState<boolean>(MosaicPageState.locked)
  const [colourMode, setColourMode] = useState<IdeaColourMode>("default")
  const openIdea: Idea | null =
    openIdeaId != null ? model.fetchIdea(openIdeaId) : null

  const setOpenIdeaIdWithEvent = (
    id: ID | null,
    selectionPos: number | null = null
  ) => {
    setOpenIdeaId(id)
    setSelectionPos(selectionPos || 0)
    if (id == null) hub.emit("idea-pane:close", {})
    else if (openIdeaId !== id) {
      hub.emit("idea-pane:open", { ideaId: id })
      commander.exec(new Commands.ViewIdeaCommand({ id, viewedAt: new Date() }))
    }
  }

  const deleteSelectedIdeas = () => {
    if (!plateView) return
    if (plateView.selectedIdeaIds.length > 0) {
      commander.exec(
        new Commands.DeleteIdeasCommand({ ids: plateView.selectedIdeaIds })
      )
      return
    }
    if (plateView.openedCell) {
      const openedIdea = model.getIdeaAt(plateView.openedCell)
      if (openedIdea) {
        commander.exec(
          new Commands.DeleteIdeasCommand({ ids: [openedIdea.id] })
        )
        return
      }
    }
  }

  useEffect(() => {
    const k = hub.on(
      "navigation-history:open-location-set",
      ({ location, selectionPos }) => {
        if (location) {
          const idea = model.getIdeaAt(location) || null
          if (idea) setOpenIdeaIdWithEvent(idea.id, selectionPos)
          else setOpenIdeaIdWithEvent(null)
        } else setOpenIdeaIdWithEvent(null)
      }
    )

    const k2 = hub.on("model:ideas-deleted", ({ ids }: { ids: ID[] }) => {
      if (openIdeaId && ids.includes(openIdeaId)) setOpenIdeaIdWithEvent(null)
    })

    if (plateView) {
      hotkeys("down,up,right,left", "mosaic", function (event, handler) {
        if (
          (event?.srcElement as HTMLElement)?.classList?.contains("ProseMirror")
        )
          return
        event.preventDefault()
        plateView.handleArrowKey(handler.key)
      })

      if (platform == "mac")
        hotkeys(
          "alt+down,alt+up,alt+right,alt+left",
          "mosaic",
          function (event, handler) {
            event.preventDefault()
            plateView.handleFastArrowKey(handler.key)
          }
        )

      hotkeys(KEYS.openFinder[platform], "mosaic", function (event, _handler) {
        event.preventDefault()
        // TODO cleanup move to page state
        window.dispatchEvent(new CustomEvent("open-fuzzy-finder", {}))
      })

      hotkeys("escape", "mosaic", function (event, handler) {
        event.preventDefault()
        // Idea pane open: close it. Otherwise clear colour filters (heatmap / self).
        if (openIdeaId != null) {
          NavigationHistoryState.set_open_location(null)
        } else if (colourMode !== "default") {
          setColourMode("default")
        }
      })

      hotkeys(KEYS.undo[platform], "mosaic", function (event, handler) {
        event.preventDefault()
        commander.undo()
      })

      hotkeys(KEYS.redo[platform], "mosaic", function (event, handler) {
        event.preventDefault()
        commander.redo()
      })

      hotkeys("backspace,delete", "mosaic", function (event, handler) {
        event.preventDefault()
        deleteSelectedIdeas()
      })

      hotkeys("u", "mosaic", function (event, handler) {
        event.preventDefault()
        setLocked(!locked)
      })

      hotkeys(
        KEYS.navigateHistoryGlob[platform],
        "mosaic",
        function (event, handler) {
          event.preventDefault()
          switch (event.key) {
            case "ArrowLeft":
              NavigationHistoryState.navigate_back()
              break
            case "ArrowRight":
              NavigationHistoryState.navigate_forward()
              break
          }
        }
      )
    }

    return () => {
      hub.remove(k)
      hub.remove(k2)
      hotkeys.unbind("down,up,right,left", "mosaic")
      hotkeys.unbind("alt+down,alt+up,alt+right,alt+left", "mosaic")
      hotkeys.unbind(KEYS.openFinder[platform], "mosaic")
      hotkeys.unbind("escape", "mosaic")
      hotkeys.unbind(KEYS.undo[platform], "mosaic")
      hotkeys.unbind(KEYS.redo[platform], "mosaic")
      hotkeys.unbind(KEYS.navigateHistoryGlob[platform], "mosaic")
      hotkeys.unbind("u", "mosaic")
    }
  }, [plateView, openIdeaId, setOpenIdeaId, locked, colourMode])

  useEffect(() => {
    if (MosaicPageState.forceOpenLocation) {
      NavigationHistoryState.set_open_location(
        MosaicPageState.forceOpenLocation
      )
      MosaicPageState.forceOpenLocation = null
    } else {
      if (initialLoadComplete)
        local?.getOpenIdeaId().then((id: ID | null) => {
          if (id) {
            const idea = model.getIdea(id)
            if (idea) NavigationHistoryState.set_open_location(idea.location)
            else setOpenIdeaIdWithEvent(null)
          } else setOpenIdeaIdWithEvent(null)
        })
    }
    local?.getViewBounds().then((vb: ViewBounds | null) => {
      setInitialViewBounds(vb)
    })
  }, [initialLoadComplete])

  hotkeys.setScope("mosaic")

  if (
    openIdeaId === undefined ||
    initialViewBounds === undefined ||
    sync?.clock === null
  )
    return null

  return (
    <div className="relative flex min-h-0 flex-auto flex-row">
      <ShowPlateView
        plateViewDraggingHappening={plateViewDraggingHappening}
        setPlateViewDraggingHappening={setPlateViewDraggingHappening}
        locked={locked}
        setLocked={setLocked}
        colourMode={colourMode}
        setColourMode={setColourMode}
        openLocation={openIdea ? openIdea.location : null}
        initialViewBounds={initialViewBounds}
      />
      {openIdeaId && (
        <IdeaPane
          ideaId={openIdeaId}
          selectionPos={selectionPos}
          setOpenIdeaId={setOpenIdeaIdWithEvent}
          plateViewDraggingHappening={plateViewDraggingHappening}
        />
      )}
      <FuzzyIdeaFinder
        showNearbyOnOpen={true}
        onChoose={(idea: Idea) => {
          if (!model || !idea) return
          NavigationHistoryState.set_open_location(idea.location)
        }}
      />
    </div>
  )
}
